import { requestNestMethod } from "../nest/client"
import type { FsWorkspaceContext } from "../nestAPIs/files"
import { type GitSummaryResponse, gitApi } from "../nestAPIs/git"
import type { Repo, SetupEnvironmentEvent, Task, TaskDeviceEnvironment } from "../types"
import { getDeviceId } from "../utils/deviceId"

export type TaskDirectoryPurpose = "working" | "original"
export type TaskDirectoryTarget = "primary" | "agent-worktree" | "manual"
export type TaskDirectorySelectionMode = "automatic" | "manual"
export type DesignatedPathState = "none" | "absent" | "inactive" | "active"
export type ManualPathState = "none" | "active" | "unavailable"

export interface TaskDirectory {
    target: TaskDirectoryTarget
    path: string
    context: FsWorkspaceContext
}

export interface TaskCommitTarget {
    directory: TaskDirectory
    headCommit: string
}

export interface TaskDirectoryView {
    working: TaskDirectory
    original: TaskDirectory
    diffBaseCommit?: string
    taskCommitTarget?: TaskCommitTarget
    status: GitSummaryResponse | null
    designatedPath?: string
    designatedPathState: DesignatedPathState
    selectionMode: TaskDirectorySelectionMode
    manualPath?: string
    manualPathState: ManualPathState
}

interface AgentGitCandidate {
    worktreePath: string
    startHead?: string
    legacyMergeBase?: string
}

const loggedResolutionErrors = new Set<string>()

function logResolutionError(taskId: string, error: unknown): void {
    if (loggedResolutionErrors.has(taskId)) return
    loggedResolutionErrors.add(taskId)
    console.warn("[TaskDirectory] Failed to resolve task directory; using original root:", { taskId, error })
}

export function unavailableGitSummary(): GitSummaryResponse {
    return {
        branch: null,
        headCommit: "",
        ahead: null,
        hasChanges: false,
        staged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
        unstaged: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
        untracked: { files: [], stats: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 } },
        total: { filesChanged: 0, binaryFiles: 0, insertions: 0, deletions: 0 },
    }
}

function legacyWorktreeDirFromSetupEvent(event: SetupEnvironmentEvent): string | null {
    const setupOutput = event.setupOutput ?? ""
    const worktreeLine = setupOutput
        .split(/\r?\n/)
        .map((line) => line.trim())
        .find((line) => line.startsWith("Worktree:"))
    const worktreeDir = worktreeLine?.slice("Worktree:".length).trim()
    return worktreeDir || event.workingDir || null
}

function latestSnapshotMergeBase(task: Task): string | undefined {
    for (let index = task.events.length - 1; index >= 0; index--) {
        const event = task.events[index]
        if (event.type === "snapshot") return event.mergeBaseCommit
    }
    return undefined
}

export function legacyTaskDeviceEnvironment(task: Task, deviceId = getDeviceId()): TaskDeviceEnvironment | null {
    const current = task.deviceEnvironments.find((environment) => environment.deviceId === deviceId)
    if (current) return current
    if (task.isolationStrategy.type !== "worktree") return null

    let setupEvent: SetupEnvironmentEvent | undefined
    for (let index = task.events.length - 1; index >= 0; index--) {
        const event = task.events[index]
        if (event.type !== "setup_environment" || event.status !== "completed") continue
        if (event.deviceId === deviceId) {
            setupEvent = event
            break
        }
    }
    if (!setupEvent && task.deviceEnvironments.length === 0) {
        for (let index = task.events.length - 1; index >= 0; index--) {
            const event = task.events[index]
            if (event.type === "setup_environment" && event.status === "completed") {
                setupEvent = event
                break
            }
        }
    }
    if (!setupEvent) return null
    const worktreeDir = legacyWorktreeDirFromSetupEvent(setupEvent)
    if (!worktreeDir) return null
    const timestamp = task.updatedAt || task.createdAt || new Date().toISOString()
    return {
        id: deviceId,
        deviceId,
        worktreeDir,
        activeLocation: "worktree",
        setupComplete: true,
        mergeBaseCommit: latestSnapshotMergeBase(task),
        createdAt: setupEvent.completedAt ?? setupEvent.createdAt ?? timestamp,
        lastUsedAt: timestamp,
    }
}

export function legacyAgentGitCandidate(task: Task): AgentGitCandidate | undefined {
    if (task.isolationStrategy.type !== "worktree") return undefined
    const environment = legacyTaskDeviceEnvironment(task)
    if (!environment?.worktreeDir || environment.activeLocation === "primary") return undefined
    return {
        worktreePath: environment.worktreeDir,
        legacyMergeBase: environment.mergeBaseCommit,
    }
}

function candidateForTask(task: Task): AgentGitCandidate | undefined {
    return task.agentGitContext
        ? {
              worktreePath: task.agentGitContext.worktreePath,
              startHead: task.agentGitContext.startHead,
          }
        : legacyAgentGitCandidate(task)
}

export function originalTaskDirectory(repo: Repo): TaskDirectory {
    return {
        target: "primary",
        path: repo.path,
        context: {
            workspaceId: repo.id,
            workspaceRoot: repo.path,
        },
    }
}

function agentTaskDirectory(repo: Repo, path: string): TaskDirectory {
    return {
        target: "agent-worktree",
        path,
        context: {
            workspaceId: repo.id,
            workspaceRoot: path,
            worktreeId: path,
        },
    }
}

function taskCommitTargetForDirectory(directory: TaskDirectory, status?: GitSummaryResponse): TaskCommitTarget | undefined {
    if (!status?.headCommit) return undefined
    return {
        directory,
        headCommit: status.headCommit,
    }
}

function manualTaskDirectory(repo: Repo, path: string, candidate?: AgentGitCandidate): TaskDirectory {
    if (path === repo.path) return originalTaskDirectory(repo)
    if (path === candidate?.worktreePath) return agentTaskDirectory(repo, path)
    return {
        target: "manual",
        path,
        context: {
            workspaceId: repo.id,
            workspaceRoot: path,
            worktreeId: path,
        },
    }
}

export function taskDirectoryForSelection(task: Task, repo: Repo, path: string): TaskDirectory {
    return manualTaskDirectory(repo, path, candidateForTask(task))
}

export function getTaskDirectory(view: TaskDirectoryView | null, repo: Repo, purpose: TaskDirectoryPurpose): TaskDirectory {
    return view?.[purpose] ?? originalTaskDirectory(repo)
}

async function readOriginalStatus(task: Task, repo: Repo, designatedPathState: DesignatedPathState, candidate?: AgentGitCandidate): Promise<TaskDirectoryView> {
    const original = originalTaskDirectory(repo)
    const status = await gitApi
        .getGitSummary({
            workspaceId: task.repoId,
            workspaceRoot: repo.path,
        })
        .catch((error) => {
            logResolutionError(task.id, error)
            return unavailableGitSummary()
        })
    const taskCommitTarget = taskCommitTargetForDirectory(original, status)
    return {
        working: original,
        original,
        diffBaseCommit: candidate?.startHead ?? candidate?.legacyMergeBase,
        ...(taskCommitTarget ? { taskCommitTarget } : {}),
        status,
        ...(candidate ? { designatedPath: candidate.worktreePath } : {}),
        designatedPathState,
        selectionMode: "automatic",
        manualPathState: "none",
    }
}

export type TaskDirectoryPathInspection = { ok: true; path: string } | { ok: false; error: string }

export async function inspectTaskDirectoryPath(path: string, _workspaceId?: string): Promise<TaskDirectoryPathInspection> {
    const requestedPath = path.trim()
    if (!requestedPath) return { ok: false, error: "Choose a directory." }

    try {
        const repository = await requestNestMethod("git.repository.path.read", { path: requestedPath })
        if (!repository.isRepo) return { ok: false, error: "Choose a Git working directory." }
        const root = repository.root || requestedPath
        return { ok: true, path: root }
    } catch {
        return { ok: false, error: "That directory is not available on this computer." }
    }
}

async function resolveDesignatedCheckout(candidate: AgentGitCandidate | undefined, selectedPath: string): Promise<DesignatedPathState> {
    if (!candidate) return "none"
    if (candidate.worktreePath === selectedPath) return "active"
    try {
        const repository = await requestNestMethod("git.repository.path.read", { path: candidate.worktreePath })
        return repository.isRepo ? "inactive" : "absent"
    } catch {
        return "absent"
    }
}

async function resolveManualTaskDirectory(task: Task, repo: Repo, requestedPath: string): Promise<TaskDirectoryView> {
    const candidate = candidateForTask(task)
    const inspection = await inspectTaskDirectoryPath(requestedPath, task.repoId)
    if (!inspection.ok) {
        const designatedPathState = await resolveDesignatedCheckout(candidate, requestedPath)
        const fallback = await readOriginalStatus(task, repo, designatedPathState, candidate)
        return {
            ...fallback,
            selectionMode: "manual",
            manualPath: requestedPath,
            manualPathState: "unavailable",
        }
    }

    const working = manualTaskDirectory(repo, inspection.path, candidate)
    const status = await gitApi
        .getGitSummary({
            workspaceId: task.repoId,
            workspaceRoot: working.context.workspaceRoot,
            ...(working.context.worktreeId ? { worktreeId: working.context.worktreeId } : {}),
        })
        .catch((error) => {
            logResolutionError(task.id, error)
            return unavailableGitSummary()
        })
    const designatedPathState = await resolveDesignatedCheckout(candidate, inspection.path)
    const diffBaseCommit = candidate?.startHead ?? candidate?.legacyMergeBase
    const taskCommitTarget = taskCommitTargetForDirectory(working, status)
    return {
        working,
        original: originalTaskDirectory(repo),
        ...(diffBaseCommit ? { diffBaseCommit } : {}),
        ...(taskCommitTarget ? { taskCommitTarget } : {}),
        status,
        ...(candidate ? { designatedPath: candidate.worktreePath } : {}),
        designatedPathState,
        selectionMode: "manual",
        manualPath: inspection.path,
        manualPathState: "active",
    }
}

export async function resolveTaskDirectoryView(task: Task, repo: Repo, isGitRepo: boolean): Promise<TaskDirectoryView> {
    const original = originalTaskDirectory(repo)
    if (task.directorySelection?.mode === "manual") {
        return resolveManualTaskDirectory(task, repo, task.directorySelection.path)
    }
    if (!isGitRepo) {
        return {
            working: original,
            original,
            status: null,
            designatedPathState: "none",
            selectionMode: "automatic",
            manualPathState: "none",
        }
    }

    const candidate = candidateForTask(task)
    if (!candidate) return readOriginalStatus(task, repo, "none")

    try {
        const repository = await requestNestMethod("git.repository.path.read", { path: candidate.worktreePath })
        if (!repository.isRepo) return readOriginalStatus(task, repo, "absent", candidate)

        const status = await gitApi.getGitSummary({
            workspaceId: task.repoId,
            workspaceRoot: candidate.worktreePath,
            worktreeId: candidate.worktreePath,
        })
        const active = status.hasChanges || (status.headCommit !== "" && status.headCommit !== (candidate.startHead ?? ""))
        if (!active) return readOriginalStatus(task, repo, "inactive", candidate)

        const taskCommitTarget = taskCommitTargetForDirectory(agentTaskDirectory(repo, candidate.worktreePath), status)
        return {
            working: agentTaskDirectory(repo, candidate.worktreePath),
            original,
            diffBaseCommit: candidate.startHead ?? candidate.legacyMergeBase,
            ...(taskCommitTarget ? { taskCommitTarget } : {}),
            status,
            designatedPath: candidate.worktreePath,
            designatedPathState: "active",
            selectionMode: "automatic",
            manualPathState: "none",
        }
    } catch (error) {
        logResolutionError(task.id, error)
        return readOriginalStatus(task, repo, "absent", candidate)
    }
}
